import { Extension, textInputRule } from "@tiptap/core";
import { TEXT_SHORTCUTS, compileShortcuts, TextShortcut } from "./shortcuts";

export interface TextShortcutsOptions {
  /** Defaults to the shared TEXT_SHORTCUTS map. */
  shortcuts: TextShortcut[];
}

/**
 * TipTap extension that turns `TEXT_SHORTCUTS` into ProseMirror input rules.
 *
 * Why input rules rather than a keydown handler — every gotcha is handled by
 * ProseMirror's built-in inputRules plugin, for free:
 *
 * - Caret: the matched trigger is replaced in place and the caret is left
 *   right after the inserted glyph (no jump to end).
 * - Undo: each replacement is one history step, so Cmd/Ctrl+Z reverts it
 *   cleanly; pressing Backspace immediately after reverts to the typed
 *   characters (ProseMirror's `undoInputRule`).
 * - IME / composition (Hebrew, CJK, etc.): the plugin skips while
 *   `view.composing` is true and only re-checks on `compositionend`, so it
 *   never fires mid-composition.
 * - Paste: input rules run on typed input only — pasted content is untouched.
 * - RTL: rules operate on document positions, not visual direction.
 *
 * Rules are applied in `compileShortcuts` order (longest trigger first) so
 * "<->" wins over the "->" it contains.
 */
export const TextShortcuts = Extension.create<TextShortcutsOptions>({
  name: "textShortcuts",

  addOptions() {
    return {
      shortcuts: TEXT_SHORTCUTS
    };
  },

  addInputRules() {
    return compileShortcuts(this.options.shortcuts).map(({ find, replace }) =>
      textInputRule({ find, replace })
    );
  }
});

export default TextShortcuts;
